// Preferences Service - Centralized persistence for user personalization settings
//
// Stores all user preferences as a single JSON object in localStorage.
// Components should use the usePreferences() hook rather than calling this service directly.

// ============================================================================
// TYPES
// ============================================================================

export interface UserPreferences {
  darkMode: boolean;
  tabMode: boolean;
  footerVisible: boolean;
  autoCollapse: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY = 'APP_USER_PREFERENCES';

const DEFAULTS: UserPreferences = {
  darkMode: false,
  tabMode: true,
  footerVisible: false,
  autoCollapse: true,
};

// ============================================================================
// SERVICE
// ============================================================================

const preferencesService = {
  /**
   * Get all preferences, merged with defaults for any missing keys.
   * Accepts optional config-based defaults to override built-in defaults.
   */
  getAll(configDefaults?: Partial<UserPreferences>): UserPreferences {
    const defaults = { ...DEFAULTS, ...configDefaults };
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return defaults;
      const parsed = JSON.parse(stored);
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  },

  /**
   * Get a single preference value.
   */
  get<K extends keyof UserPreferences>(
    key: K,
    configDefaults?: Partial<UserPreferences>
  ): UserPreferences[K] {
    return this.getAll(configDefaults)[key];
  },

  /**
   * Set a single preference value and persist.
   */
  set<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): void {
    const current = this.getAll();
    current[key] = value;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    } catch {
      // Storage full or unavailable — fail silently
    }
  },

  /**
   * Update multiple preferences at once and persist.
   */
  setMany(updates: Partial<UserPreferences>): void {
    const current = this.getAll();
    const merged = { ...current, ...updates };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch {
      // Storage full or unavailable — fail silently
    }
  },

  /**
   * Reset all preferences to defaults and clear storage.
   */
  reset(): void {
    localStorage.removeItem(STORAGE_KEY);
  },
};

export default preferencesService;
